import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import { libraryShape, stackMapShape } from '@bibliocommons/bc-prop-types';
import { IconButton } from '@bibliocommons/base-buttons';
import { ZoomIn, ZoomOut, Print } from '@bibliocommons/base-icons';
import { openPopUpWindow } from '@bibliocommons/utils-browser';
import { WarningAlert } from '@bibliocommons/alerts';
import Svg from '@bibliocommons/svg';

import PanZoom from 'app/components/shared/PanZoom';

import './StackMapDisplay.scss';

const MAX_SCALE_FACTOR = 5;
const STACK_MAP_WINDOW_NAME = 'stack-map';

function renderPlaceHolder() {
  return (
    <Svg className="stack-map-placeholder" width="709" height="96" preserveAspectRatio="none" viewBox="0 0 709 96">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M365,84 L365,96 L0,96 L0,84 L365,84 Z M650,60 L650,72 L0,72 L0,60 L650,60 Z M709,36 L709,48 L0,48 L0,36 L709,36 Z M155,0 L155,16 L0,16 L0,0 L155,0 Z"
      />
    </Svg>
  );
}

export default class StackMapDisplay extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      x: 0,
      y: 0,
      scale: 1
    };
    bindAll(this);
  }

  handleMapUpdate(coordinates) {
    this.setState(coordinates);
  }

  handleZoomIn() {
    this.setState(state => ({
      scale: Math.min(MAX_SCALE_FACTOR, state.scale + 0.5)
    }));
  }

  handleZoomOut() {
    this.setState(state => {
      const newScale = Math.max(1, state.scale - 0.5);
      return {
        scale: newScale,
        x: newScale !== 1 ? state.x : 0,
        y: newScale !== 1 ? state.y : 0
      };
    });
  }

  handlePrint() {
    const stackMap = this.props.stackMap;
    const stackMapSettings = this.props.currentLibrary.get('stackMap');
    const queryParams = new URLSearchParams({
      callno: stackMap.get('callno'),
      library: stackMap.get('library'),
      location: stackMap.get('location'),
      v: 'pf'
    }).toString();

    openPopUpWindow({
      url: `https://${stackMapSettings.get('domain')}.stackmap.com/view/?${queryParams}`,
      STACK_MAP_WINDOW_NAME,
      windowFeatures: {
        height: 800,
        width: 950,
        directories: 0
      }
    });
  }

  renderMapControls() {
    const zoomIn = (
      <IconButton className="zoom-in" dataKey="zoom-in-button" handleClick={this.handleZoomIn}>
        <ZoomIn />
      </IconButton>
    );

    const zoomOut = (
      <IconButton className="zoom-out" dataKey="zoom-out-button" handleClick={this.handleZoomOut}>
        <ZoomOut />
      </IconButton>
    );

    const print = (
      <IconButton className="print" dataKey="print-button" handleClick={this.handlePrint}>
        <Print />
      </IconButton>
    );

    return (
      <div className="stack-map-controls">
        {zoomIn}
        {zoomOut}
        {print}
      </div>
    );
  }

  renderImage() {
    const maps = this.props.stackMap.getIn(['maps', 0], Immutable.Map());
    const { scale, x, y } = this.state;

    return (
      <div className="stack-map-panel" aria-hidden>
        <PanZoom
          className="stack-map-image-wrapper"
          handleUpdate={this.handleMapUpdate}
          height={maps.get('height')}
          maxScale={MAX_SCALE_FACTOR}
          scale={scale}
          x={x}
          y={y}
          width={maps.get('width')}
        >
          <img
            className="stack-map-image"
            data-key="stack-map-image"
            alt={maps.get('library')}
            src={`${maps.get('mapurl')}&marker=1`}
            loading="eager"
          />
        </PanZoom>
      </div>
    );
  }

  renderContent() {
    const { currentLibrary, stackMap, stackMapTransactions } = this.props;

    if (stackMapTransactions.get('noResults') || !stackMap) {
      return (
        <WarningAlert className="failure-warning">
          <FormattedMessage id="stack_map_failure" />
        </WarningAlert>
      );
    }

    const isShowRanges = !currentLibrary.getIn(['stackMap', 'hideRanges']);

    const callNumber = stackMap.get('callno');
    const collection = stackMap.get('location');

    const map = stackMap.getIn(['maps', 0]) ?? Immutable.Map();
    const directions = map.get('directions');
    const floorName = map.get('floorname');
    const ranges = map.get('ranges') ?? [];

    const hasGeneralInfo = Boolean(directions || callNumber || collection);

    return (
      <section>
        <div className="shelf-details">
          {hasGeneralInfo && (
            <div className="shelf-details-general">
              {directions && <p className="shelf-detail shelf-detail--primary">{directions}</p>}
              {(callNumber || collection) && (
                <p className="shelf-detail shelf-detail--secondary">
                  {callNumber && (
                    <>
                      <FormattedMessage defaultMessage="Call number" id="CALL_NUMBER" />: {callNumber}
                      <br />
                    </>
                  )}
                  {collection && (
                    <>
                      <FormattedMessage defaultMessage="Collection" id="collection" />: {collection}
                    </>
                  )}
                </p>
              )}
            </div>
          )}
          {isShowRanges ? (
            <ul className="ranges">
              {ranges.map(range => {
                const section = range.get('rangename');
                const callNumberStart = range.get('startcallno');
                const callNumberEnd = range.get('endcallno');

                return (
                  <li className="shelf-detail shelf-detail--primary" key={`${callNumberStart}-${callNumberEnd}`}>
                    <FormattedMessage
                      defaultMessage="{floorName}. Section: {section}. Call numbers from ''{callNumberStart}'' to ''{callNumberEnd}''."
                      id="stackmap_floor_location"
                      values={{ callNumberEnd, callNumberStart, floorName, isFloorOnly: false, section }}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="shelf-detail shelf-detail--primary">
              <FormattedMessage
                defaultMessage="{floorName}."
                id="stackmap_floor_location"
                values={{ floorName, isFloorOnly: true }}
              />
            </p>
          )}
          <p className="shelf-detail shelf-detail--secondary">
            <FormattedMessage defaultMessage="The red pin indicates the location of the item." id="stackmap_legend" />
          </p>
        </div>
        {this.renderMapControls()}
        {this.renderImage()}
        <div className="powered-by">
          <FormattedMessage id="stackmap_powered_by" />
        </div>
      </section>
    );
  }

  render() {
    return (
      <div className="cp-stack-map-display">
        {this.props.stackMapTransactions.get('isFetching') ? renderPlaceHolder() : this.renderContent()}
      </div>
    );
  }
}

StackMapDisplay.propTypes = {
  currentLibrary: libraryShape.isRequired,
  stackMap: stackMapShape,
  stackMapTransactions: ImmutablePropTypes.mapContains({
    open: PropTypes.bool,
    noResults: PropTypes.bool,
    isFetching: PropTypes.bool
  }).isRequired
};
